/* Ayudas de formato para el correo en la pantalla de ingreso.
 *
 * OJO CON LO QUE **NO** ESTÁ ACÁ: la lista de quién puede entrar. Vive en la
 * tabla `accesos_permitidos` de Supabase y se consulta con la función
 * `estado_de_acceso`.
 *
 * Estuvo escrita acá y se sacó a propósito. Con la lista en el código, sumar a
 * alguien nuevo pedía un commit y un deploy — o sea que en la práctica lo hacía
 * una sola persona, cuando se acordaba. Y peor: la misma regla terminaba
 * duplicada en el front y en la base, que es la forma segura de que se
 * desincronicen. El síntoma sería feo y tardío: alguien recibe el alta, crea su
 * clave, y recién al volver descubre que no puede entrar.
 *
 * Para agregar a alguien ahora, un INSERT en `accesos_permitidos`. Sin deploy.
 */

export function normalizarMail(valor) {
  // Los mails no distinguen mayúsculas, así que se normaliza siempre y en
  // todos lados. La base también lo hace por su cuenta (hay un trigger), pero
  // acá evita que el usuario vea escrito algo distinto de lo que se compara.
  return (valor || "").trim().toLowerCase();
}

/* «aalcaraz» → «aalcaraz@cantarini.com.ar» (pedido de Igna, 01/09): la
 * enorme mayoría entra con el correo de la empresa, y tipear el dominio en el
 * teléfono es la parte que sale mal. Si ya trae una @ —un Gmail, el propio
 * dominio escrito entero— no se toca nada. */
export function completarDominio(valor) {
  const mail = normalizarMail(valor);
  if (!mail || mail.includes("@")) return mail;
  return mail + DOMINIO_EMPRESA;
}

export function esMailValido(valor) {
  const mail = normalizarMail(valor);
  // Deliberadamente laxo: alcanza con que tenga forma de mail. Quién entra lo
  // decide la base, no una expresión regular barroca.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail);
}

/* Textos de cada respuesta de `estado_de_acceso`. Están juntos para que se lean
 * como conjunto: son lo único que ve alguien que no puede entrar, y entre los
 * cuatro no tienen que dejar a nadie sin saber qué hacer después. */

/* El dominio de la empresa, SÓLO para nombrarlo en el cartel y para completar
 * lo que se tipea sin arroba. La lista de quién entra sigue viviendo en
 * `accesos_permitidos` y no acá — esto es texto de ayuda, no una regla, y por
 * eso no hay ningún chequeo que lo use. */
export const DOMINIO_EMPRESA = "@cantarini.com.ar";

/* SON DOS, Y EL SEGUNDO APARECIÓ EL 15/09/2026.
 *
 * `transportescantarini.com.ar` es de la misma empresa: los MX de los dos
 * apuntan al mismo servidor (gwcan.cantarini.com.ar). Lo usa la gente de Río
 * Grande. Está habilitado en `accesos_permitidos` como dominio entero.
 *
 * `completarDominio` SIGUE COMPLETANDO CON EL PRIMERO y no con éste, a
 * propósito: es el de la enorme mayoría, y lo que se completa solo tiene que
 * acertarle casi siempre. Quien es del segundo escribe la dirección entera —y
 * por eso el cartel de abajo nombra los dos, que es lo que le dice que puede.
 * Si algún día el reparto se da vuelta, esto es lo que hay que cambiar. */
export const DOMINIOS_EMPRESA = [
  DOMINIO_EMPRESA,
  "@transportescantarini.com.ar",
];

/* EL CARTEL NOMBRA LOS DOMINIOS, Y ES UN ARREGLO CON UN CASO DETRÁS.
 *
 * Decía «si es tu correo de trabajo y creés que es un error, escribile a
 * sistemas para que te den de alta», y a alguien que entró con su Gmail
 * personal eso lo manda a pedir un alta **que ya tenía**: la cuenta existía
 * hacía siete semanas, con permisos y con clave provisoria. El mensaje era
 * cierto —ese correo no tiene acceso— y llevaba a hacer lo que no había que
 * hacer, que es peor que uno equivocado: nadie lo discute.
 *
 * Con los dominios escritos se corrige solo. El subtítulo ya dice «tu correo
 * de la empresa», pero justo cuando se lo erra deja de decir cuál es. Y desde
 * el 15/09/2026 son dos: nombrar uno solo manda a la mitad de Río Grande a
 * probar una dirección que no tiene.
 *
 * ── Y NO SE SUGIERE LA CUENTA, a propósito ─────────────────────────────────
 *
 * La tentación es contestar «¿no será pgaribay@…?», que es lo que hace
 * `mensajeCorreoParecido`. Ahí se puede porque quien llegó a ese estado ya
 * escribió una dirección de un dominio habilitado; acá lo escribe CUALQUIERA
 * sin sesión, y sugerir una cuenta confirmaría que existe. Es lo mismo que
 * `estado_de_acceso` evita devolviendo una sola palabra y nunca un nombre.
 *
 * El dominio no es un dato de nadie: está en la firma de todos los mails. */
export const MENSAJE_NO_AUTORIZADO =
  `Este correo no tiene acceso. Si trabajás acá, entrá con tu correo ` +
  `${DOMINIOS_EMPRESA.join(" o ")} — puede que ya tengas cuenta con una de ` +
  `esas direcciones. Si ése es tu correo de trabajo y creés que es un error, ` +
  `escribile a sistemas.`;

/* La cuenta existe y alguien con `admin.permisos` la dio de baja (05/10). No se
 * dice quién ni por qué: eso lo sabe quien la dio de baja, y es a quien hay que
 * ir a preguntarle. */
export const MENSAJE_DE_BAJA =
  "Tu cuenta del hub está dada de baja. Si creés que es un error, escribile " +
  "a sistemas.";

export const MENSAJE_SIN_CUENTA =
  "Tu correo está habilitado, pero todavía no te dieron de alta. Pedíselo a " +
  "sistemas y te van a pasar una contraseña provisoria.";

/* Este mensaje sale sólo cuando la base encontró una cuenta que se escribe casi
 * igual. Le gana a MENSAJE_SIN_CUENTA porque son dos situaciones opuestas: una
 * es "falta hacerte el alta", la otra es "el alta está hecha y le erraste a una
 * letra". Marcos recibió la primera teniendo la clave provisoria encima. */
export const mensajeCorreoParecido = (sugerido) =>
  `No encontramos esa dirección. ¿No será ${sugerido}?`;
